import * as React from 'react'
import {
  FaIcon,
  faPrint,
  faFileInvoice,
} from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DatePickerRangeProps } from '@/modules/drivers/types'
import SearchInput from '@/modules/drivers/shared/SearchInput'
import SearchColor from '@/assets/icons_new/SearchColor.svg'
import usePrintViolations from '../../violations_new/hooks/usePrintViolations'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'

type Props = {
  onCalculate?: () => void
  range?: DatePickerRangeProps | null
  companyId: string
  onDateRangeSubmit?: (dateRange: DatePickerRangeProps) => void
  search?: string
  setSearch?: (search: string) => void
  searchResults?: number
}

const ViolationsActionBar: React.FC<Props> = ({
  onCalculate,
  range,
  onDateRangeSubmit,
  search = '',
  setSearch,
  companyId,
  searchResults = 0,
}) => {
  const { t } = useLocalize()
  const { busyTooltip, handlePrint, handlePrintInstruction } =
    usePrintViolations()
  const { drivers, selectedIds } = useDrivers()

  const [datePickerOpen, setDatePickerOpen] = React.useState(false)
  const [showSearch, setShowSearch] = React.useState(false)
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const searchContainerRef = React.useRef<HTMLDivElement>(null)

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !datePickerOpen
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
    }
    setDatePickerOpen(willOpen)
  }

  const handleDateRangeSubmit = (dateRange: DatePickerRangeProps) => {
    if (onDateRangeSubmit) {
      onDateRangeSubmit(dateRange)
    }
    setDatePickerOpen(false)
  }

  React.useEffect(() => {
    if (!datePickerOpen) return undefined

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDatePickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [datePickerOpen])

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        if (setSearch) setSearch('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [setSearch])
  return (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-relative" ref={searchContainerRef}>
          <div
            className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-px-1 ${showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'}`}
            onClick={() => setShowSearch((prev) => !prev)}
          >
            <img src={SearchColor} alt="Search Icon" className="tw-px-1" />
            <p className="tw-text-sm tw-font-medium tw-text-primary">
              {t('common.search')}
            </p>
          </div>
          {showSearch && setSearch && (
            <SearchInput
              search={search}
              searchResults={searchResults}
              setSearch={setSearch}
              placeholder={t('driverModule.search-violations') || ''}
              id="search-violations-input"
            />
          )}
        </div>
        <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>
        <div className="tw-flex tw-items-center tw-gap-1">
          <p>{t('common.print')} {t('common.for')}</p>
          <p className="tw-font-semibold">2. {t('driverModule.driver-genitive')}</p>
          <p>:</p>
        </div>
        <Tooltip
          text={t('driverModule.print-violations-with-activities')}
          onClick={() =>
            handlePrint({
              selectedIds: selectedIds,
              drivers: drivers,
              companyId: companyId,
            })
          }
          blockedText={busyTooltip}
        >
          <div className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faPrint}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
            <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
              {t('driverModule.print-violations-with-activities')}
            </span>
          </div>
        </Tooltip>
        <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]"></div>

        <Tooltip
          text={t('driverModule.print-instruction')}
          onClick={() =>
            handlePrintInstruction({
              selectedIds: selectedIds,
              drivers: drivers,
              companyId: companyId,
            })
          }
          blockedText={busyTooltip}
        >
          <div className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faFileInvoice}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
            <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
              {t('driverModule.print-instruction')}
            </span>
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

export default ViolationsActionBar
